import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  integrationService,
  type ConnectionRepository,
  type IntegrationConnection,
  type IntegrationTarget,
} from '@services/integrationService';
import { apiErrorCode } from '@utils/apiError';

interface Props {
  orgId: string;
  orgSlug: string;
  contextId: string;
}

/** What the connection's repository listing came back with. */
type RepositoryLookup = 'idle' | 'loading' | 'ready' | 'rejected' | 'unavailable' | 'forbidden';

function targetRepository(target: IntegrationTarget | null): ConnectionRepository | null {
  if (!target?.config) return null;
  const { owner, repo } = target.config;
  return { owner, name: repo, full_name: `${owner}/${repo}`, private: false };
}

/**
 * REV-514: choose which repository THIS tracked place exports its findings to.
 *
 * The organization-level Integrations tab is authentication ("who are we, to
 * GitHub"). This card is the destination ("where do this place's issues go"),
 * which is what lets per-finding "Export to GitHub" resolve without asking the
 * user every time.
 *
 * REV-697: the repository is chosen from what the connection can reach, so the
 * owner is never asked for. Listing them is also the test of the connection.
 */
export default function IntegrationTargetCard({ orgId, orgSlug, contextId }: Props) {
  const { t } = useTranslation();

  const [connections, setConnections] = useState<IntegrationConnection[]>([]);
  const [target, setTarget] = useState<IntegrationTarget | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [connectionId, setConnectionId] = useState('');
  const [repository, setRepository] = useState<ConnectionRepository | null>(null);
  const [repositories, setRepositories] = useState<ConnectionRepository[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [lookup, setLookup] = useState<RepositoryLookup>('idle');
  const latestLookup = useRef(0);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [conns, targets] = await Promise.all([
        integrationService.list(orgId),
        integrationService.listTargets(orgId, contextId),
      ]);
      // Only GitHub connections can be a target; the API refuses any other.
      const usable = conns.filter((c) => c.provider === 'github');
      setConnections(usable);
      const github = targets.find((x) => x.provider === 'github') ?? null;
      setTarget(github);
      // Seed the form from the existing target so an edit starts from reality
      // rather than from a blank picker.
      setConnectionId(github?.connection_id ?? usable[0]?.id ?? '');
      setRepository(targetRepository(github));
    } catch {
      // A failed fetch and "nothing configured" are different states. Rendering
      // the empty form for both would hide a real error behind a normal-looking
      // blank card.
      setLoadError(t('trackedPlaces.integrationTarget.loadFailed', 'Could not load integrations.'));
    } finally {
      setLoading(false);
    }
  }, [orgId, contextId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadRepositories = useCallback(
    async (id: string) => {
      // A slower answer for a connection the user has moved on from is dropped.
      latestLookup.current += 1;
      const thisLookup = latestLookup.current;
      setLookup('loading');
      // The last connection's list must not be offered under this one.
      setRepositories([]);
      setTruncated(false);
      try {
        const listed = await integrationService.listRepositories(orgId, id);
        if (thisLookup !== latestLookup.current) return;
        setRepositories(listed.repositories);
        setTruncated(listed.truncated);
        setLookup('ready');
      } catch (err) {
        if (thisLookup !== latestLookup.current) return;
        setRepositories([]);
        setTruncated(false);
        const code = apiErrorCode(err);
        if (code === 'ROLE_REQUIRED') setLookup('forbidden');
        else if (code === 'github_token_invalid') setLookup('rejected');
        else setLookup('unavailable');
      }
    },
    [orgId]
  );

  useEffect(() => {
    if (connectionId) void loadRepositories(connectionId);
  }, [connectionId, loadRepositories]);

  // A current target stays selectable when the listing no longer includes it,
  // so the picker never shows a value it has no option for.
  const options = useMemo(
    () =>
      repository && !repositories.some((r) => r.full_name === repository.full_name)
        ? [repository, ...repositories]
        : repositories,
    [repositories, repository]
  );

  const handleConnectionChange = (id: string) => {
    setConnectionId(id);
    setSaved(false);
    setRepository(target?.connection_id === id ? targetRepository(target) : null);
  };

  const lookupHelp = () => {
    if (lookup === 'loading') {
      return t('trackedPlaces.integrationTarget.repoLoading', 'Loading repositories…');
    }
    if (lookup !== 'ready') return undefined;
    return truncated
      ? t(
          'trackedPlaces.integrationTarget.repoTruncated',
          'Connected. Showing the first {{count}} repositories.',
          { count: repositories.length }
        )
      : t('trackedPlaces.integrationTarget.repoCount', 'Connected. {{count}} repositories.', {
          count: repositories.length,
        });
  };

  const handleSave = async () => {
    // Validate on submit, never by pre-disabling Save: a disabled button cannot
    // tell the user what is missing (platform form rule).
    setSaved(false);
    if (!connectionId || !repository) {
      setFormError(
        t('trackedPlaces.integrationTarget.required', 'Choose a connection and a repository.')
      );
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      const next = await integrationService.setTarget(orgId, contextId, {
        provider: 'github',
        connectionId,
        config: { owner: repository.owner, repo: repository.name },
      });
      setTarget(next);
      setSaved(true);
    } catch {
      setFormError(
        t(
          'trackedPlaces.integrationTarget.saveFailed',
          'Could not save. Check the repository exists and the connected account can write to it.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    setFormError(null);
    setSaved(false);
    try {
      await integrationService.removeTarget(orgId, contextId, 'github');
      setTarget(null);
      setRepository(null);
    } catch {
      setFormError(
        t('trackedPlaces.integrationTarget.removeFailed', 'Could not remove the repository.')
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Skeleton variant="text" width={220} height={32} />
          <Skeleton variant="rectangular" height={120} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('trackedPlaces.integrationTarget.title', 'Issue tracker')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'trackedPlaces.integrationTarget.description',
            'Findings exported from this tracked place are filed here. The account is connected once for the whole organization; each tracked place chooses its own repository.'
          )}
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {!loadError && connections.length === 0 && (
          <Alert severity="info">
            {t(
              'trackedPlaces.integrationTarget.noConnection',
              'No provider is connected for this organization yet. Connect one first, then choose a repository here.'
            )}{' '}
            <Link component={RouterLink} to={`/orgs/${orgSlug}/integrations`}>
              {t('trackedPlaces.integrationTarget.goToIntegrations', 'Go to Integrations')}
            </Link>
          </Alert>
        )}

        {!loadError && connections.length > 0 && (
          <>
            {target?.config && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={`${target.config.owner}/${target.config.repo}`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}

            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            {lookup === 'forbidden' && (
              <Alert severity="info">
                {t(
                  'trackedPlaces.integrationTarget.forbidden',
                  'Only organization owners and admins can choose the repository.'
                )}
              </Alert>
            )}
            {lookup === 'rejected' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {t(
                  'trackedPlaces.integrationTarget.tokenRejected',
                  "GitHub rejected this connection's token. Reconnect it, then choose a repository here."
                )}{' '}
                <Link component={RouterLink} to={`/orgs/${orgSlug}/integrations`}>
                  {t('trackedPlaces.integrationTarget.goToIntegrations', 'Go to Integrations')}
                </Link>
              </Alert>
            )}
            {lookup === 'unavailable' && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void loadRepositories(connectionId)}
                  >
                    {t('trackedPlaces.integrationTarget.retry', 'Try again')}
                  </Button>
                }
              >
                {t(
                  'trackedPlaces.integrationTarget.repoUnavailable',
                  'Could not get the repository list from GitHub.'
                )}
              </Alert>
            )}
            {lookup === 'ready' && repositories.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t(
                  'trackedPlaces.integrationTarget.repoNone',
                  'This connection cannot reach a repository that accepts issues. Check the token has access to one.'
                )}
              </Alert>
            )}
            {saved && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('trackedPlaces.integrationTarget.saved', 'Repository saved.')}
              </Alert>
            )}

            {lookup !== 'forbidden' && (
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="integration-connection-label">
                    {t('trackedPlaces.integrationTarget.connection', 'Connection')}
                  </InputLabel>
                  <Select
                    labelId="integration-connection-label"
                    label={t('trackedPlaces.integrationTarget.connection', 'Connection')}
                    value={connectionId}
                    onChange={(e) => handleConnectionChange(e.target.value)}
                  >
                    {connections.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.config
                          ? `${c.provider} — ${c.config.owner}/${c.config.repo}`
                          : c.provider}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Autocomplete
                  options={options}
                  value={repository}
                  loading={lookup === 'loading'}
                  loadingText={t(
                    'trackedPlaces.integrationTarget.repoLoading',
                    'Loading repositories…'
                  )}
                  noOptionsText={t(
                    'trackedPlaces.integrationTarget.repoNoMatch',
                    'No matching repository'
                  )}
                  getOptionLabel={(option) => option.full_name}
                  isOptionEqualToValue={(option, value) => option.full_name === value.full_name}
                  onChange={(_event, next) => {
                    setRepository(next);
                    setSaved(false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('trackedPlaces.integrationTarget.repo', 'Repository')}
                      helperText={lookupHelp()}
                      FormHelperTextProps={{ role: 'status' }}
                    />
                  )}
                />

                <Stack direction="row" spacing={2}>
                  <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <CircularProgress size={20} />
                    ) : (
                      t('trackedPlaces.integrationTarget.save', 'Save')
                    )}
                  </Button>
                  {target && (
                    <Button color="error" onClick={handleRemove} disabled={saving}>
                      {t('trackedPlaces.integrationTarget.remove', 'Remove')}
                    </Button>
                  )}
                </Stack>
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
